import { makeStyles } from "@material-ui/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { CheckboxGroup } from "../CheckboxGroup";
import { FreeTextQuestion } from "../FreeTextQuestion";

const useStyles = makeStyles((theme: any) => ({
  checkboxesContainer: {
    lineHeight: "32px",
    margin: "8px -24px 0",
    minHeight: 32
  },
  checkboxesWrapper: {
    display: "block",
    width: "100%"
  },
  container: {
    background: "#ffffff",
    border: "1px solid #e6e6e6",
    borderRadius: 3,
    display: "flex" as "flex",
    flexDirection: "column" as "column",
    flexWrap: "wrap" as "wrap",
    minHeight: "100%",
    width: "100%"
  },
  question: {
    "& > span": {
      display: "inline-block" as "inline-block",
      lineHeight: 1,
      verticalAlign: "middle" as "middle"
    },
    color: "#4d4d4e",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.2rem"
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "1.4rem"
    },
    lineHeight: "4.6rem",
    minHeight: "46px",
    [theme.breakpoints.up("xl")]: {
      lineHeight: "3.7rem",
      minHeight: "37px"
    }
  },
  questionContainer: {
    borderBottom: "1px solid #e6e6e6",
    display: "flex" as "flex",
    flexGrow: 1,
    flexWrap: "wrap" as "wrap",
    height: "auto",
    padding: "24px 23px 0"
  },
  textContainer: {
    color: "#909090",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.2rem"
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "1.4rem"
    }
  }
}));

interface IValue {
  category: number | null;
  value: string | null;
}

interface ICheckboxGroup {
  question: string;
  value: string;
}

interface IOwnProps {
  readOnly: boolean;
  invalid?: boolean;
  placeholder?: string;
  value: IValue;
  vertical: boolean;
  options: ICheckboxGroup[];
  onChange?: (value: IValue) => void;
  label: string;
  userName?: string;
}

export const LabelGroupQuestion = ({
  readOnly,
  invalid = false,
  vertical,
  options,
  userName = "",
  value = {
    category: null,
    value: null
  },
  label,
  onChange
}: IOwnProps) => {
  const { value: textValue, category } = value;
  const classes = useStyles();
  const { t } = useTranslation();

  const handleCheckboxChange = React.useCallback(
    (newValue: number) => {
      if (onChange) {
        onChange({
          category: newValue,
          value: textValue
        });
      }
    },
    [onChange, textValue]
  );

  const handleTextChange = React.useCallback(
    (newValue: string) => {
      if (onChange) {
        onChange({
          category,
          value: newValue
        });
      }
    },
    [onChange, category]
  );

  const [categoryInvalid, textInvalid] = React.useMemo(() => {
    if (invalid) {
      if (category === null) {
        return [true, false];
      } else if (textValue === null || textValue.trim().length === 0) {
        return [false, true];
      } else {
        return [false, false];
      }
    } else {
      return [false, false];
    }
  }, [invalid, textValue, category]);

  const checkboxesOptions = React.useMemo(() => {
    return options.map(({ value: optionValue }) => optionValue);
  }, [options]);

  const [boldLabel, textLabel] = React.useMemo(() => {
    const separatorIndex = label.indexOf(" - ");
    if (separatorIndex > -1) {
      return [label.slice(0, separatorIndex), label.slice(separatorIndex, label.length)];
    } else {
      return [null, label];
    }
  }, [label]);

  const optionLabel = React.useMemo(() => {
    if (typeof category === "number" && options[category]) {
      return options[category].question;
    } else {
      return null;
    }
  }, [options, category]);

  return (
    <div data-qa-selector="companyValueGroupQuestion" className={classes.container}>
      <div className={classes.questionContainer}>
        <div className={classes.textContainer}>
          {boldLabel ? <b>{boldLabel}</b> : null}
          {textLabel}
        </div>
        <div className={classes.checkboxesWrapper}>
          <div className={classes.checkboxesContainer}>
            <CheckboxGroup
              readOnly={readOnly}
              onChange={handleCheckboxChange}
              value={category}
              options={checkboxesOptions}
              vertical={vertical}
              invalid={categoryInvalid}
            />
          </div>
          <div className={classes.question}>
            <span>
              {category !== null &&
                optionLabel &&
                t(optionLabel, {
                  firstName: userName,
                  name: userName
                })}
            </span>
          </div>
        </div>
      </div>
      <FreeTextQuestion
        value={textValue || ""}
        onChange={handleTextChange}
        readOnly={readOnly}
        invalid={textInvalid}
        placeholder={
          readOnly
            ? t(`feedback.companyValue.noneSubmitted`)
            : t(`feedback.companyValue.placeholder`, {
                firstName: userName,
                name: userName
              })
        }
      />
    </div>
  );
};
